// src/components/professor/SettingsSection.js

import React, { useState, useEffect } from "react";
import { Switch } from "@headlessui/react";
import { auth, db } from "../../firebase";
import {
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
} from "firebase/auth";
import {
  doc,
  deleteDoc,
  writeBatch,
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  getDoc,
  arrayUnion,
  arrayRemove,
} from "firebase/firestore";
import Preloader from "../../components/Preloader";

const SettingsSection = () => {
  const [loading, setLoading] = useState(false);

  // Tabs: "account" or "classes"
  const [activeTab, setActiveTab] = useState("classes");

  // -----------------------------
  // Account Settings
  // -----------------------------
  const handleResetPassword = async () => {
    setLoading(true);
    try {
      const user = auth.currentUser;
      if (user && user.email) {
        await sendPasswordResetEmail(auth, user.email);
        alert("Password reset email sent. Please check your inbox.");
      } else {
        alert("No authenticated user found.");
      }
    } catch (error) {
      console.error("Error sending password reset email:", error);
      alert("Failed to send password reset email. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (
      window.confirm(
        "Are you sure you want to delete your account? This action cannot be undone."
      )
    ) {
      const password = prompt(
        "Please enter your password to confirm account deletion:"
      );
      if (!password) {
        alert("Password is required to delete account.");
        return;
      }

      setLoading(true);
      try {
        const user = auth.currentUser;
        if (user && user.email) {
          // Reauthenticate user
          await signInWithEmailAndPassword(auth, user.email, password);

          // Initialize Firestore
          const batch = writeBatch(db);
          const collectionsToDelete = ["classes", "enrollments", "grades"];

          // Delete any documents belonging to this user in those collections
          for (const collectionName of collectionsToDelete) {
            const qDel = query(
              collection(db, collectionName),
              where("professor_uid", "==", user.uid)
            );
            const querySnapshot = await getDocs(qDel);
            querySnapshot.forEach((docSnap) => {
              batch.delete(doc(db, collectionName, docSnap.id));
            });
          }
          await batch.commit();

          // Delete settings doc
          await deleteDoc(doc(db, "settings", user.uid));

          // Finally, delete user from Firebase Auth
          await user.delete();

          alert("Account deleted successfully.");
          window.location.href = "/sign-up";
        } else {
          alert("No authenticated user found.");
        }
      } catch (error) {
        console.error("Error deleting account:", error);
        alert(
          "Failed to delete account. Please ensure your password is correct and try again."
        );
      } finally {
        setLoading(false);
      }
    }
  };

  // -----------------------------
  // Class Settings
  // -----------------------------
  const [classes, setClasses] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState("");
  const [classDetails, setClassDetails] = useState({
    class_name: "",
    class_invitation: "",
    start_date: "",
    end_date: "",
    statsaccess: false,
    gradeaccess: false,
    sessionaccess: false,
    aiReporting: false,
    quizFormat: "fixed",
  });

  const [accessEmail, setAccessEmail] = useState("");
  const [accessUsers, setAccessUsers] = useState([]);

  useEffect(() => {
    // Fetch classes for the logged-in user
    const fetchClasses = async () => {
      setLoading(true);
      try {
        const user = auth.currentUser;
        if (!user) {
          alert("No user is logged in.");
          setLoading(false);
          return;
        }
        const qClasses = query(
          collection(db, "classes"),
          where("professor_uid", "==", user.uid)
        );
        const querySnapshot = await getDocs(qClasses);
        const classesData = querySnapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        setClasses(classesData);
      } catch (error) {
        console.error("Error fetching classes:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchClasses();
  }, []);

  const fetchAccessUsers = async (classId) => {
    try {
      const classDocSnap = await getDoc(doc(db, "classes", classId));
      if (classDocSnap.exists()) {
        const data = classDocSnap.data();
        const accessUids = data.access_uid || [];

        // Fetch user details for each UID
        const accessUsersData = await Promise.all(
          accessUids.map(async (uid) => {
            const userDoc = await getDoc(doc(db, "users", uid));
            if (userDoc.exists()) {
              const userData = userDoc.data();
              return {
                uid: uid,
                email: userData.email || "Email not available",
              };
            } else {
              return {
                uid: uid,
                email: "User not found",
              };
            }
          })
        );
        setAccessUsers(accessUsersData);
      } else {
        alert("Class not found.");
        setAccessUsers([]);
      }
    } catch (error) {
      console.error("Error fetching access users:", error);
      setAccessUsers([]);
    }
  };

  const handleClassChange = async (e) => {
    const classId = e.target.value;
    setSelectedClassId(classId);

    if (classId) {
      setLoading(true);
      try {
        const classDocSnap = await getDoc(doc(db, "classes", classId));
        if (classDocSnap.exists()) {
          const data = classDocSnap.data();
          setClassDetails({
            class_name: data.class_name || "",
            class_invitation: data.class_invitation || "",
            start_date: data.start_date
              ? data.start_date.toDate().toISOString().substr(0, 10)
              : "",
            end_date: data.end_date
              ? data.end_date.toDate().toISOString().substr(0, 10)
              : "",
            statsaccess: data.statsaccess || false,
            gradeaccess: data.gradeaccess || false,
            sessionaccess: data.sessionaccess || false,
            aiReporting: data.aiReporting || false,
            quizFormat: data.quizFormat || "fixed",

          });
          // Fetch access users
          await fetchAccessUsers(classId);
        } else {
          alert("Class not found.");
          resetClassDetails();
        }
      } catch (error) {
        console.error("Error fetching class details:", error);
      } finally {
        setLoading(false);
      }
    } else {
      resetClassDetails();
    }
  };

  const resetClassDetails = () => {
    setClassDetails({
      class_name: "",
      class_invitation: "",
      start_date: "",
      end_date: "",
      statsaccess: false,
      gradeaccess: false,
      sessionaccess: false,
      aiReporting: false,
      quizFormat: "fixed",
    });
    setAccessUsers([]);
  };

  const handleClassDetailChange = (e) => {
    const { name, value, type, checked } = e.target;
    setClassDetails((prevDetails) => ({
      ...prevDetails,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleClassSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = auth.currentUser;
      if (!user) {
        alert("No user is logged in.");
        setLoading(false);
        return;
      }

      // Check if the invitation code changed
      const classDocRef = doc(db, "classes", selectedClassId);
      const classDocSnap = await getDoc(classDocRef);
      const originalData = classDocSnap.data();
      const normalizedInvitationCode = classDetails.class_invitation
        .trim()
        .toUpperCase();

      if (!normalizedInvitationCode) {
        alert("Class invitation code is required.");
        setLoading(false);
        return;
      }

      // If changed, ensure it's unique
      if (normalizedInvitationCode !== originalData.class_invitation) {
        const qInv = query(
          collection(db, "classes"),
          where("class_invitation", "==", normalizedInvitationCode)
        );
        const qSnapshot = await getDocs(qInv);
        if (!qSnapshot.empty) {
          alert(
            "Class invitation code is already in use. Please choose another one."
          );
          setLoading(false);
          return;
        }
      }

      // Update class details
      const updatedData = {
        class_name: classDetails.class_name,
        class_invitation: normalizedInvitationCode,
        start_date: classDetails.start_date
          ? new Date(classDetails.start_date)
          : null,
        end_date: classDetails.end_date ? new Date(classDetails.end_date) : null,
        statsaccess: classDetails.statsaccess,
        gradeaccess: classDetails.gradeaccess,
        sessionaccess: classDetails.sessionaccess,
        aiReporting: classDetails.aiReporting,
        quizFormat: classDetails.quizFormat,
      };
      await updateDoc(classDocRef, updatedData);

      alert("Class details updated successfully.");
    } catch (error) {
      console.error("Error updating class details:", error);
      alert("Failed to update class details. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // -----------------------------
  // Access Management
  // -----------------------------
  const handleGrantAccess = async () => {
    if (!accessEmail.trim()) {
      alert("Please enter a valid email.");
      return;
    }
    setLoading(true);
    try {
      const usersRef = collection(db, "users");
      const qUser = query(usersRef, where("email", "==", accessEmail.trim()));
      const querySnapshot = await getDocs(qUser);

      if (querySnapshot.empty) {
        alert("No user found with the provided email.");
        setLoading(false);
        return;
      }

      const userDoc = querySnapshot.docs[0];
      const userData = userDoc.data();
      if (userData.role !== "professor") {
        alert("Only users with the role of professor can be granted access.");
        setLoading(false);
        return;
      }

      const targetUid = userDoc.id;
      if (accessUsers.some((u) => u.uid === targetUid)) {
        alert("This user already has access to the class.");
        setLoading(false);
        return;
      }

      // Add the user's UID to the access_uid array
      const classDocRef = doc(db, "classes", selectedClassId);
      await updateDoc(classDocRef, {
        access_uid: arrayUnion(targetUid),
      });

      setAccessUsers((prev) => [
        ...prev,
        { uid: targetUid, email: userData.email },
      ]);
      alert("Access granted successfully.");
      setAccessEmail("");
    } catch (error) {
      console.error("Error granting access:", error);
      alert("Failed to grant access. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveAccess = async (uid) => {
    if (!window.confirm("Are you sure you want to revoke access for this user?")) {
      return;
    }
    setLoading(true);
    try {
      const classDocRef = doc(db, "classes", selectedClassId);
      await updateDoc(classDocRef, {
        access_uid: arrayRemove(uid),
      });
      setAccessUsers((prev) => prev.filter((user) => user.uid !== uid));
      alert("Access revoked successfully.");
    } catch (error) {
      console.error("Error revoking access:", error);
      alert("Failed to revoke access. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // -----------------------------
  // Render
  // -----------------------------
  return (
    <section
      id="settings-section"
      className="flex flex-col items-center justify-start min-h-screen p-8"
      style={{ backgroundColor: "var(--section-bg)", color: "var(--text-color)" }}
    >
      {loading && <Preloader />}

      {/* Tabs */}
      <ul className="flex border-b border-[var(--border-color)] w-full max-w-3xl mb-4">
        <li>
          <button
            className={`px-4 py-2 -mb-px border-b-2 transition-colors ${activeTab === "classes"
                ? "border-[var(--primary-color)] text-[var(--primary-color)]"
                : "border-transparent hover:border-[var(--border-color)] text-[var(--muted-color)]"
              }`}
            onClick={() => setActiveTab("classes")}
          >
            Class Settings
          </button>
        </li>
        <li className="mr-4">
          <button
            className={`px-4 py-2 -mb-px border-b-2 transition-colors ${activeTab === "account"
                ? "border-[var(--primary-color)] text-[var(--primary-color)]"
                : "border-transparent hover:border-[var(--border-color)] text-[var(--muted-color)]"
              }`}
            onClick={() => setActiveTab("account")}
          >
            Account Settings
          </button>
        </li>
      </ul>

      <div className="w-full max-w-3xl">
        {/* ACCOUNT TAB */}
        {activeTab === "account" && (
          <div className="flex flex-col space-y-8">
            <div>
              <h3 className="text-xl font-bold mb-4">Account Security</h3>
              <button
                onClick={handleResetPassword}
                className="px-4 py-2 bg-[var(--primary-color)] hover:bg-[var(--hover-color)] text-white rounded"
              >
                Reset Password
              </button>
            </div>

            <div>
              <h3 className="text-xl font-bold mb-4">Account Management</h3>
              <button
                onClick={handleDeleteAccount}
                className="px-4 py-2 bg-[var(--primary-color)] hover:bg-[var(--hover-color)] text-white rounded"
              >
                Delete Account
              </button>
            </div>
          </div>
        )}

        {/* CLASSES TAB */}
        {activeTab === "classes" && (
          <div className="flex flex-col space-y-8">
            <div>
              <p className="mb-2">
                Select a class to edit its details. (If you are an admin, your classes will appear in the dropdown below.)
              </p>
              <select
                value={selectedClassId}
                onChange={handleClassChange}
                className="w-full px-3 py-2 border border-[var(--border-color)] rounded-md
                         bg-[var(--input-bg)] text-[var(--text-color)]
                         focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]
                         focus:border-transparent transition-all"
              >
                <option value="">Select a class</option>
                {classes.map((cls) => (
                  <option key={cls.id} value={cls.id}>
                    {cls.class_name}
                  </option>
                ))}
              </select>
            </div>

            {selectedClassId && (
              <>
                <form
                  onSubmit={handleClassSave}
                  className="flex flex-col space-y-4 bg-[var(--card-bg)] p-4 rounded shadow text-[var(--text-color)]"
                >
                  <div className="flex flex-col md:flex-row md:items-center">
                    <label
                      htmlFor="class_name"
                      className="md:w-1/3 mb-2 md:mb-0 md:text-right md:pr-4"
                    >
                      Class Name:
                    </label>
                    <input
                      type="text"
                      id="class_name"
                      name="class_name"
                      value={classDetails.class_name}
                      onChange={handleClassDetailChange}
                      className="p-2 border border-[var(--border-color)] rounded w-full md:w-2/3 bg-[var(--input-bg)] text-[var(--text-color)] focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]
                           focus:border-transparent transition-all"
                      required
                    />
                  </div>

                  <div className="flex flex-col md:flex-row md:items-center">
                    <label
                      htmlFor="class_invitation"
                      className="md:w-1/3 mb-2 md:mb-0 md:text-right md:pr-4"
                    >
                      Class Invitation Code:
                    </label>
                    <input
                      type="text"
                      id="class_invitation"
                      name="class_invitation"
                      value={classDetails.class_invitation}
                      onChange={handleClassDetailChange}
                      className="p-2 border border-[var(--border-color)] rounded w-full md:w-2/3 bg-[var(--input-bg)] text-[var(--text-color)] focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]
                           focus:border-transparent transition-all"
                      required
                    />
                  </div>

                  <div className="flex flex-col md:flex-row md:items-center">
                    <label
                      htmlFor="start_date"
                      className="md:w-1/3 mb-2 md:mb-0 md:text-right md:pr-4"
                    >
                      Start Date:
                    </label>
                    <input
                      type="date"
                      id="start_date"
                      name="start_date"
                      value={classDetails.start_date}
                      onChange={handleClassDetailChange}
                      className="p-2 border border-[var(--border-color)] rounded w-full md:w-2/3 bg-[var(--input-bg)] text-[var(--text-color)] focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]
                           focus:border-transparent transition-all"
                    />
                  </div>

                  <div className="flex flex-col md:flex-row md:items-center">
                    <label
                      htmlFor="end_date"
                      className="md:w-1/3 mb-2 md:mb-0 md:text-right md:pr-4"
                    >
                      End Date:
                    </label>
                    <input
                      type="date"
                      id="end_date"
                      name="end_date"
                      value={classDetails.end_date}
                      onChange={handleClassDetailChange}
                      className="p-2 border border-[var(--border-color)] rounded w-full md:w-2/3 bg-[var(--input-bg)] text-[var(--text-color)] focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]
                           focus:border-transparent transition-all"
                    />
                  </div>

                  {/* — Slide-toggles for Stats, Grade, Session & AI — */}
                  <div className="flex flex-col md:flex-row md:items-start">
                    <label className="md:w-1/3 mb-2 md:mb-0 md:text-right md:pr-4">
                      Access & Features:
                    </label>
                    <div className="md:w-2/3 space-y-4">
                      {[
                        { name: "statsaccess", label: "Stats Access" },
                        { name: "gradeaccess", label: "Grade Access" },
                        { name: "sessionaccess", label: "Session Access" },
                        // { name: "aiReporting", label: "AI Reporting After Each Session" },
                      ].map(({ name, label }) => (
                        <Switch.Group
                          key={name}
                          as="div"
                          className="flex items-center justify-between"
                        >
                          <Switch.Label
                            className="text-[var(--text-color)] font-medium"
                          >
                            {label}
                          </Switch.Label>
                          <Switch
                            checked={classDetails[name]}
                            onChange={(val) =>
                              setClassDetails((d) => ({ ...d, [name]: val }))
                            }
                            className={`
    ce-mini-switch relative inline-flex items-center h-4 w-7 rounded-full transition-colors
    border border-[var(--border-color)]
    focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]
    ${classDetails[name]
                                ? 'bg-[var(--primary-color)]'
                                : 'bg-[var(--muted-color)]'
                              }
  `}
                          >
                            <span
                              className={`
      ce-mini-switch-knob inline-block h-2.5 w-2.5 transform rounded-full transition-transform shadow-sm
      bg-[var(--card-bg)]
      ${classDetails[name]
                                  ? 'translate-x-4'
                                  : 'translate-x-0.5'
                                }
    `}
                            />
                          </Switch>

                        </Switch.Group>
                      ))}
                    </div>
                  </div>

                  {/* — Quiz Format — */}
                  <div className="flex flex-col md:flex-row md:items-center my-4">
                    <label
                      htmlFor="quizFormat"
                      className="md:w-1/3 mb-2 md:mb-0 md:text-right md:pr-4 text-[var(--text-color)] font-medium"
                    >
                      Quiz Format:
                    </label>
                    <select
                      id="quizFormat"
                      name="quizFormat"
                      value={classDetails.quizFormat}
                      onChange={handleClassDetailChange}
                      className="md:w-2/3 p-2 border border-[var(--border-color)] rounded focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)] transition-all"
                    >
                      <option value="fixed">5 Fixed Multiple-Choice</option>
                      <option value="dynamic">Dynamic Input</option>
                    </select>
                  </div>


                  <div className="flex justify-end">
                    <button
                      type="submit"
                      className="px-4 py-2 bg-[var(--primary-color)] hover:bg-[var(--hover-color)] text-white rounded"
                    >
                      Save Changes
                    </button>
                  </div>
                </form>

                {/* Access Management Section */}
                <div className="mt-6">
                  <h4 className="text-lg font-semibold mb-2">Manage Access</h4>
                  <div className="flex flex-col md:flex-row md:items-center space-y-2 md:space-y-0 md:space-x-2">
                    <input
                      type="email"
                      placeholder="Enter professor's email"
                      value={accessEmail}
                      onChange={(e) => setAccessEmail(e.target.value)}
                      className="p-2 border border-[var(--border-color)] rounded bg-[var(--input-bg)] text-[var(--text-color)] w-full md:w-auto focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]
                           focus:border-transparent transition-all"
                    />
                    <button
                      onClick={handleGrantAccess}
                      className="px-4 py-2 bg-[var(--primary-color)] hover:bg-[var(--hover-color)] text-white rounded"
                    >
                      Grant Access
                    </button>
                  </div>

                  <h5 className="text-md font-semibold mt-4 mb-2">
                    Current Access Users:
                  </h5>
                  {accessUsers.length === 0 ? (
                    <p className="text-[var(--muted-color)]">
                      No users have been granted access.
                    </p>
                  ) : (
                    <table className="min-w-full border border-[var(--border-color)] mt-2">
                      <thead className="bg-[var(--alt-bg)]">
                        <tr>
                          <th className="py-2 px-4 border-b border-[var(--border-color)] text-left">
                            Email
                          </th>
                          <th className="py-2 px-4 border-b border-[var(--border-color)] text-left">
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {accessUsers.map((user) => (
                          <tr
                            key={user.uid}
                            className="hover:bg-[var(--card-hover-bg)] transition-colors"
                          >
                            <td className="py-2 px-4 border-b border-[var(--border-color)]">
                              {user.email}
                            </td>
                            <td className="py-2 px-4 border-b border-[var(--border-color)]">
                              <button
                                onClick={() => handleRemoveAccess(user.uid)}
                                className="px-3 py-1 bg-[var(--primary-color)] hover:bg-[var(--hover-color)] text-white rounded text-sm"
                              >
                                Remove
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </section>
  );

};

export default SettingsSection;
